// @flow
import invariant from 'invariant';

import type { User } from './user';
import type { EventSink } from './user-session-tracer/event-sink';
import type {
  Navigate,
  UserEvent,
  WhiteboardReduxAction,
  InteractionAction,
} from './user-session-tracer/user-event';
import {
  type Specification as SinkSpecification,
  fromSpecification,
} from './user-session-tracer/sink';
import {
  navigateEvent,
  whiteboardReduxAction,
  interactionAction,
} from './user-session-tracer/user-event';

export type Specification = $ReadOnly<{|
  sinks: $ReadOnlyArray<SinkSpecification>,
|}>;

/**
 * Basic multiplexer for user session event tracing.
 *
 * Provides basic event API and supports plexing to various analytics event sinks. Generally
 * intended to be used as a singleton. The default export of this module is a function that
 * receives the singleton instance.
 *
 * When sending events, try to send relevant contextual information but don't send any
 * Personal Data / Personally Identifiable Information / PII. Unfortunately, what is and what
 * is not PII changes based on region and is not well-defined. Just to be safe:
 *
 * - If it appears on the User model, don't send it.
 * - If it appears on the Customer model, don't send it.
 *
 * There is some amount of automated PII filtering that happens beyond this point, but it
 * needs to be updated statically.
 *
 * User information should never be sent with individual events, it should only be set via
 * the `UserSessionTracer#setUser`. This will handle associating a user to events.
 *
 * @see https://en.wikipedia.org/wiki/Personal_data
 *
 * @example
 *    import userSessionTracer from 'modules/user-session-tracer';
 *
 *    userSessionTracer().navigate({ to: 'Some Page' });
 *    userSessionTracer().interaction({ interaction: 'Click Button', context: 'whatever' });
 */
export class UserSessionTracer {
  specification: Specification | null;

  sinks: $ReadOnlyArray<EventSink> | null;

  user: User | null;

  client: string | null;

  eventCache: Array<UserEvent>;

  constructor() {
    // eslint-disable-next-line fp/no-mutation
    this.eventCache = [];
    // eslint-disable-next-line fp/no-mutation
    this.user = null;
    // eslint-disable-next-line fp/no-mutation
    this.client = null;
  }

  sendEventToSinks(event: UserEvent) {
    invariant(
      this.sinks,
      'UserSessionTracer must be initialized before attempting to send events to sinks.',
    );
    this.sinks.forEach((sink) =>
      sink.handleEvent(event, this.user, this.client),
    );
  }

  flushEventCache() {
    this.eventCache.forEach((event) => this.sendEventToSinks(event));
    // eslint-disable-next-line fp/no-mutation
    this.eventCache = [];
  }

  initialize(specification: Specification) {
    // eslint-disable-next-line fp/no-mutation
    this.specification = specification;
    // eslint-disable-next-line fp/no-mutation
    this.sinks = specification.sinks.map(fromSpecification);

    this.flushEventCache();
  }

  cacheEvent(event: UserEvent) {
    // eslint-disable-next-line fp/no-mutating-methods
    this.eventCache.push(event);
  }

  handleEvent(event: UserEvent) {
    if (!this.sinks) {
      this.cacheEvent(event);
      return;
    }

    this.sendEventToSinks(event);
  }

  setUser(user: User) {
    // eslint-disable-next-line fp/no-mutation
    this.user = user;
  }

  setClient(client: string) {
    // eslint-disable-next-line fp/no-mutation
    this.client = client;
  }

  /**
   * Send a page navigation event, indicating that the user has navigated to a page.
   */
  navigate(payload: $PropertyType<Navigate, 'payload'>) {
    this.handleEvent(navigateEvent(payload));
  }

  /**
   * Send an event for a generic interaction.
   */
  interaction(payload: $PropertyType<InteractionAction, 'payload'>) {
    this.handleEvent(interactionAction(payload));
  }

  /**
   * Send an event for an action from the whiteboard redux store.
   */
  whiteboardReduxAction(
    payload: $PropertyType<WhiteboardReduxAction, 'payload'>,
  ) {
    this.handleEvent(whiteboardReduxAction(payload));
  }
}

// eslint-disable-next-line fp/no-let
let instance: UserSessionTracer | null = null;

/**
 * Get the singleton instance of UserSessionTracer.
 *
 * When sending events, try to send relevant contextual information but don't send any
 * Personal Data / Personally Identifiable Information / PII. Unfortunately, what is and what
 * is not PII changes based on region and is not well-defined. Just to be safe:
 *
 * - If it appears on the User model, don't send it.
 * - If it appears on the Customer model, don't send it.
 *
 * There is some amount of automated PII filtering that happens beyond this point, but it
 * needs to be updated statically.
 *
 * User information should never be sent with individual events, it should only be set via
 * the `UserSessionTracer#setUser`. This will handle associating a user to events.
 *
 * @see https://en.wikipedia.org/wiki/Personal_data
 *
 * @example
 *    import userSessionTracer from 'modules/user-session-tracer';
 *
 *    userSessionTracer().navigate({ to: 'Some Page' });
 *    userSessionTracer().interaction({ interaction: 'Click Button', context: 'whatever' });
 */
export const getInstance: (void) => UserSessionTracer = () => {
  if (!instance) {
    // eslint-disable-next-line fp/no-mutation
    instance = new UserSessionTracer();
  }

  return instance;
};

export default getInstance;
